import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { saveAs } from 'file-saver';

import { waitForPaint } from 'lib/helpers/waitForPaint';

import { showAlert } from 'components/shared/Alert/Alert';

// Sections a document marks with `data-pdf-block` are never split across PDF pages (htmlToPdf block mode)
const PDF_BLOCK_SELECTOR = '[data-pdf-block]';

/**
 * Generic Download / Email / Text actions for any printable document rendered to
 * a ref. Owns the print ref, busy state, and lazy PDF rendering; the caller wires
 * the backend by passing `emailRequest` / `textRequest` and the identifying
 * `sendParams`. Email and text send a link to the document, never the file itself.
 *
 * @param {Object} opts
 * @param {string} [opts.filename] downloaded PDF filename
 * @param {string} [opts.noun] subject used in alert copy (e.g. "estimate")
 * @param {Object} [opts.sendParams] identifying params merged into every send (e.g. `{ appointmentId }`); required for email/text
 * @param {() => Promise<boolean>} [opts.downloadRequest] backend PDF download; when passed, the document is rendered server-side instead of in the browser
 * @param {(navigate, params) => Promise<{ code?: number, error?: string }>} [opts.emailRequest] email API call
 * @param {(navigate, params) => Promise<{ code?: number, error?: string }>} [opts.textRequest] text API call
 * @param {string} [opts.pageFormat] page format — 'letter' for documents that already carry the 816x1056 letter geometry
 * @param {number} [opts.margin] page margin in mm; documents with their own page margins pass 0
 * @param {number} [opts.captureWidth] CSS px width the document is laid out at for the PDF (see htmlToPdf `width`)
 * @returns {{ contentRef, busy, handleDownload, handleSend }}
 * @param {Object} [opts.pdfOptions] htmlToPdf option overrides (unit, format, orientation, scale, …) merged over the defaults
 * @returns {{ contentRef, busy, handleDownload, handleSend, withPdfBlob }}
 */

export const useDocumentShare = ({
  filename = 'Document.pdf',
  noun = 'document',
  sendParams,
  downloadRequest,
  emailRequest,
  textRequest,
  pageFormat = 'a4',
  margin = 6,
  captureWidth,
  pdfOptions
} = {}) => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'download' | 'email' | 'text' | null
  const inFlightRef = useRef(false);

  // Loaded on the click, not on render: htmlToPdf pulls html2canvas + jsPDF behind it.
  const buildPdf = async (output) => {
    // Let the button's spinner paint before rendering takes over the main thread.
    await waitForPaint();
    const { htmlToPdf } = await import('lib/helpers/pdf/htmlToPdf/htmlToPdf');
    return htmlToPdf(contentRef.current, {
      unit: 'mm',
      format: pageFormat,
      margin,
      scale: 3,
      imageQuality: 0.98,
      blockSelector: PDF_BLOCK_SELECTOR,
      output,
      width: captureWidth,
      ...pdfOptions
    });
  };

  /**
   * Single-flight guard + busy state + failure alert, shared by every render path
   * so `downloadRequest`, `handleDownload` and `withPdfBlob` can't overlap.
   *
   * @param {string} name value exposed as `busy` while the action runs
   * @param {() => Promise<any>} run
   */
  const runExclusive = async (name, run) => {
    if (inFlightRef.current) return;
    try {
      inFlightRef.current = true;
      setBusy(name);
      await run();
    } catch (_e) {
      showAlert({
        color: 'danger',
        title: `Couldn't generate the ${noun} PDF. Please try again.`
      });
    } finally {
      inFlightRef.current = false;
      setBusy(null);
    }
  };

  /**
   * Renders the PDF and hands the blob to `action`, sharing the same busy state,
   * single-flight guard and failure alert as `handleDownload`. Lets callers plug
   * in their own delivery (printing, uploading) without duplicating any of that.
   *
   * @param {string} name value exposed as `busy` while the action runs
   * @param {(blob: Blob) => any} action
   */
  const withPdfBlob = (name, action) =>
    runExclusive(name, async () => action(await buildPdf('blob')));

  const handleDownload = () =>
    runExclusive('download', async () => {
      if (downloadRequest) {
        const succeeded = await downloadRequest();
        if (!succeeded) throw new Error('download failed');
        return;
      }
      saveAs(await buildPdf('blob'), filename);
    });

  const handleSend = async (channel) => {
    if (inFlightRef.current) return;
    const request = channel === 'email' ? emailRequest : textRequest;
    const hasTarget = sendParams && Object.values(sendParams).some((value) => value != null);
    if (!request || !hasTarget) {
      showAlert({ color: 'danger', title: `Couldn't send the ${noun} — missing details` });
      return;
    }
    try {
      inFlightRef.current = true;
      setBusy(channel);
      const res = await request(navigate, { ...sendParams });
      if (res?.code === 0) {
        const subject = noun.charAt(0).toUpperCase() + noun.slice(1);
        showAlert({
          color: 'success',
          title: channel === 'email' ? `${subject} emailed` : `${subject} sent by text`
        });
      } else {
        showAlert({ color: 'danger', title: res?.error || `Couldn't send the ${noun}` });
      }
    } catch (_e) {
      showAlert({ color: 'danger', title: `Couldn't send the ${noun}. Please try again.` });
    } finally {
      inFlightRef.current = false;
      setBusy(null);
    }
  };

  return { contentRef, busy, handleDownload, handleSend, withPdfBlob };
};
